import type { ShellPersona } from '../app-shell/moduleRegistry';
import type { CognitoProfile } from '../utils/cognito-profile';

export type LogRocketIdentityInput = {
  profile: CognitoProfile | null;
  effectivePersona: ShellPersona;
  basePersona: ShellPersona;
  authStatus?: string;
};

export type LogRocketIdentityPayload = {
  userId: string;
  dedupeKey: string;
  traits: {
    name: string;
    email: string;
    cognitoSub: string;
    persona: ShellPersona;
    basePersona: ShellPersona;
    groups: string;
    provider: string;
    isGoogleProvider: boolean;
    isIntellioUser: boolean;
    isSuperAdmin: boolean;
    authStatus: string;
    app: 'ystnj';
    environment: 'production' | 'sandbox';
  };
};

const normalizeEmail = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const resolveEnvironment = (): 'production' | 'sandbox' => {
  return import.meta.env.PROD ? 'production' : 'sandbox';
};

export const buildLogRocketIdentityPayload = (
  input: LogRocketIdentityInput,
): LogRocketIdentityPayload | null => {
  const email = normalizeEmail(input.profile?.email);
  if (!email) return null;

  const userId = `${email}::${input.effectivePersona}`;
  const groups = Array.isArray(input.profile?.groups) ? input.profile.groups.filter(Boolean) : [];
  const authStatus = String(input.authStatus ?? 'unknown').trim() || 'unknown';

  const traits: LogRocketIdentityPayload['traits'] = {
    name: String(input.profile?.name ?? email).trim() || email,
    email,
    cognitoSub: String(input.profile?.userSub ?? '').trim(),
    persona: input.effectivePersona,
    basePersona: input.basePersona,
    groups: groups.join(','),
    provider: String(input.profile?.provider ?? '').trim(),
    isGoogleProvider: Boolean(input.profile?.isGoogleProvider),
    isIntellioUser: Boolean(input.profile?.isIntellioUser),
    isSuperAdmin: Boolean(input.profile?.isSuperAdmin),
    authStatus,
    app: 'ystnj',
    environment: resolveEnvironment(),
  };

  const dedupeKey = JSON.stringify({
    userId,
    cognitoSub: traits.cognitoSub,
    persona: traits.persona,
    authStatus: traits.authStatus,
  });

  return {
    userId,
    dedupeKey,
    traits,
  };
};
